<section class="cart-section">
  <div class="container">
    <div *ngIf="isLoading" class="loading-container">
      <mat-spinner></mat-spinner>
      <p>{{ "page.order.cart.loading" | translate }}</p>
    </div>
    <ng-container *ngIf="realCartItems$ | async as realCartItems">
      <div class="cart-container" *ngIf="!isLoading">
        <h1 class="cart-title">{{ "page.order.cart.title" | translate }}</h1>

        <!-- Empty Cart -->
        <div *ngIf="realCartItems.length === 0" class="empty-cart">
          <div class="empty-icon">🛒</div>
          <h2>{{ "page.order.cart.empty.title" | translate }}</h2>
          <p>{{ "page.order.cart.empty.subtitle" | translate }}</p>
          <button class="browse-btn" routerLink="/risks">{{ "page.landing.main.button" | translate }}</button>
        </div>

        <!-- Cart Content -->
        <div *ngIf="realCartItems.length > 0" class="cart-content">
          <!-- Left Side: Cart Items -->
          <div class="cart-items-container">
            <!-- Real cart -->
            <div class="cart-section-block">
              <h2 class="cart-section-title">{{ "page.order.cart.realCart.title" | translate }}</h2>
              <p class="cart-section-subtitle" *ngIf="activeSubscriptionId$ | async">
                {{ "page.order.cart.realCart.subtitle" | translate }}
              </p>

              <div class="cart-items">
                <div *ngFor="let item of realCartItems" class="cart-item">
                  <div class="item-info">
                    <div class="item-details">
                      <h3>{{ item.name }}</h3>
                      <p>{{ item.description }}</p>

                      <!-- Prescription -->
                      <div *ngIf="item.prescriptionRequired" class="prescription">
                        <a
                          href="https://slbo.be/wp-content/uploads/2017/03/2677-v9-i0-b1-fo-pre-30007-bon-de-demande-5.pdf"
                          target="_blank"
                          class="prescription-download"
                          aria-label="Download prescription"
                        >
                          <mat-icon>file_download</mat-icon>
                          {{ "page.home.predispositions.downloadPrescription" | translate }}
                        </a>
                      </div>

                      <button class="remove-btn" (click)="removeItem(item.id)">{{ "page.order.cart.remove" | translate }}</button>
                    </div>
                  </div>

                  <div class="item-price">
                    <span class="price">
                      {{ item.pricePerUnit.value | minorUnitCurrency: item.pricePerUnit.currency }}
                    </span>
                    <span *ngIf="item.pricePerUnit.timing" class="subscription-badge subscription-badge--active">
                      {{
                        "page.order.timing." + item.pricePerUnit.timing.periodUnit
                          | pluralTranslate: item.pricePerUnit.timing.period
                          | lowercase
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reserve cart -->
            <ng-container *ngIf="reserveCartItems$ | async as reserveCartItems">
              <div class="cart-section-block cart-section-block--reserve" *ngIf="reserveCartItems.length > 0">
                <h2 class="cart-section-title cart-section-title--reserve">
                  {{ "page.order.cart.reserveCart.title" | translate }}
                </h2>
                <p class="cart-section-subtitle">{{ "page.order.cart.reserveCart.subtitle" | translate }}</p>

                <div class="cart-items cart-items--reserve">
                  <div *ngFor="let item of reserveCartItems" class="cart-item cart-item--reserve">
                    <div class="item-info">
                      <div class="item-details">
                        <h3>{{ item.name }}</h3>
                        <p>{{ item.description }}</p>

                        <!-- Prescription -->
                        <div *ngIf="item.prescriptionRequired" class="prescription">
                          <a
                            href="https://slbo.be/wp-content/uploads/2017/03/2677-v9-i0-b1-fo-pre-30007-bon-de-demande-5.pdf"
                            target="_blank"
                            class="prescription-download"
                            aria-label="Download prescription"
                          >
                            <mat-icon>file_download</mat-icon>
                            {{ "page.home.predispositions.downloadPrescription" | translate }}
                          </a>
                        </div>

                        <div class="reserve-actions">
                          <button class="promote-btn" (click)="swapToCart(item.id)">
                            <mat-icon>swap_horiz</mat-icon>
                            {{ "page.order.cart.swap" | translate }}
                          </button>
                          <button class="remove-btn" (click)="removeItem(item.id)">{{ "page.order.cart.remove" | translate }}</button>
                        </div>
                      </div>
                    </div>

                    <div class="item-price">
                      <span class="price price--muted">
                        {{ item.pricePerUnit.value | minorUnitCurrency: item.pricePerUnit.currency }}
                      </span>
                      <span *ngIf="item.pricePerUnit.timing" class="subscription-badge">
                        {{
                          "page.order.timing." + item.pricePerUnit.timing.periodUnit
                            | pluralTranslate: item.pricePerUnit.timing.period
                            | lowercase
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>

          <!-- Right Side: Order Summary -->
          <div class="order">
            <div class="order-summary">
              <h2 class="summary-title">{{ "page.order.summary.title" | translate }}</h2>

              <!-- Due today detail -->
              <div class="summary-row" *ngFor="let item of realCartItems">
                <span>{{ item.name }}</span>
                <span>{{ item.pricePerUnit.value | minorUnitCurrency: item.pricePerUnit.currency }}</span>
              </div>

              <!-- Total due today -->
              <div class="summary-row divider">
                <span>{{ "page.order.summary.totalDueToday" | translate | uppercase }}</span>
                <span>{{ dueToday$ | async | minorUnitCurrency: realCartItems[0].pricePerUnit.currency }}</span>
              </div>

              <!-- Total recurring total -->
              <ng-container *ngIf="recurringCharge$ | async as recurring">
                <div *ngIf="recurring" class="subscription-summary">
                  <div class="summary-row divider">
                    <span>{{ "page.order.summary.totalRecurring" | translate | uppercase }}</span>
                  </div>

                  <div class="summary-row">
                    <span>
                      {{ "page.order.timing." + recurring.timing.periodUnit | pluralTranslate: recurring.timing.period }}
                    </span>
                    <span>{{ recurring.amount | minorUnitCurrency: realCartItems[0].pricePerUnit.currency }}</span>
                  </div>
                </div>
              </ng-container>

              <!-- Tax Notice -->
              <div class="tax-notice">
                <span class="badge-icon">ℹ️</span>
                <span>{{ "page.order.summary.taxesIncluded" | translate }}</span>
              </div>

              <!-- Checkout Button -->
              <button class="checkout-btn" *ngIf="sessionService.isLoggedIn" (click)="goToCheckout()">
                {{ "page.order.cart.goToCheckout" | translate }}
              </button>

              <!-- Login/Register Required -->
              <div *ngIf="!sessionService.isLoggedIn" class="auth-required">
                <p class="auth-message">{{ "page.order.cart.needAccount" | translate }}</p>
                <div class="auth-buttons">
                  <button class="login-button" routerLink="/login">{{ "button.login" | translate }}</button>
                  <button class="signup-button" routerLink="/registration">{{ "button.signup" | translate }}</button>
                </div>
              </div>

              <!-- Security Badge -->
              <div class="security-badge">
                <span class="badge-icon">🔒</span>
                <span>{{ "page.order.cart.secure" | translate }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </div>
</section>
